import React, { useEffect, useCallback, useState } from 'react';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';

import './_ReseedPlan.scss';
import type { AppThunkDispatch } from 'src/store.types';
import type { AppState } from 'src/reducers';
import { connect } from 'react-redux';
import { SeedPlan, SeedActuals } from 'src/state/scope/ScopeManagement.slice';
import { getScopeReadyData } from 'src/state/scope/Scope.types';
import { isEmpty, Dictionary, isUndefined } from 'lodash';
import { PlanId } from 'src/state/scope/codecs/PlanMetadata';
import { useHandleKeyPress } from 'src/utils/Component/hooks/hooks';
import AnchorRadioSelect from 'src/components/AnchorRadioSelect/AnchorRadioSelect';
import { serverScopeMemberFromId } from 'src/components/Mfp/MfpScopeSelector/MfpScopeUtils';
import { seedScope } from 'src/state/scope/Scope.actions';
import { toast } from 'react-toastify';
import { Command } from 'src/state/scope/codecs/Commands';
import { hasCommands, anchorsFromCommandDetails, getSeedCommands } from 'src/state/scope/codecs/Commands.utils';
import {
  useSelectedPlanId,
  useCurrentCommands,
  useSelectedTime,
  useSetPlanId,
  useCommandToDropdown,
  useCommandToTimeDropdown,
  useSelectedCommand,
  useHandleChangeCommand,
  useHandleChangeTime,
} from 'src/utils/Component/hooks/PlanManagement.hooks';
import { keyToCommand } from 'src/state/scope/codecs/projections/PlanMetadataToDropdown';
import { KeyboardArrowDown } from '@material-ui/icons';
import { CardActions } from '@material-ui/core';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';

const mapStateToProps = (state: AppState) => {
  const { settings } = state;
  const dimensionLabel = settings.dimensionLabelProperty;
  const readyScope = getScopeReadyData(state.mfpScope);

  if (!readyScope || isEmpty(readyScope.commands)) {
    return {
      commands: undefined,
    };
  }

  // map it twice, as hasCommands doesn't return the correct type
  // filter so that we don't get empty sets of commands
  const commands = readyScope.commands.map(getSeedCommands).filter(hasCommands).map(getSeedCommands);

  return {
    commands,
    dimensionLabel,
    scope: readyScope,
    initializedPlans: readyScope.mainConfig.initializedPlans,
    anchor: serverScopeMemberFromId(anchorsFromCommandDetails(commands), readyScope.mainConfig.members),
    isMultiScope: readyScope.isMultiScope,
    entries: settings.entriesByKey,
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    dispatchedSeedScope: (seed: Command['command']) => dispatch(seedScope(seed)),
  };
};

export type ReseedPlanModalProps = ReseedPlanModalValueProps & ReseedPlanModalDispatchProps & ReseedPlanModalOwnProps;
export type ReseedPlanModalValueProps = ReturnType<typeof mapStateToProps>;
export type ReseedPlanModalDispatchProps = ReturnType<typeof mapDispatchToProps>;
export interface ReseedPlanModalOwnProps {
  loading: boolean;
  onSubmit: (id?: number) => void;
  onCancel: () => void;
  onOpen: () => void;
}

// these are not actaully sorted correctly, so this might not actually be the correct choice
// TODO: get the default options in the following perference RP > OP > actuals
export const getDefaultOptionFromTime = (
  time: string,
  seeds: Dictionary<(SeedPlan | SeedActuals)[]>,
): SeedPlan | SeedActuals => seeds[time].reverse()[0];

export const getPlanIdFromTimeAndName = (
  time: string,
  name: string,
  seeds: Dictionary<(SeedPlan | SeedActuals)[]>,
): PlanId => {
  const foundPlanId = seeds[time].find((pln) => pln.name === name);
  if (!foundPlanId || !foundPlanId.planId) {
    throw new Error('Looked for a PlanId but couldnt find one. This shouldnt happen.');
  }
  return foundPlanId.planId;
};

function ReseedPlanModal(props: ReseedPlanModalProps) {
  const { dimensionLabel, commands, onCancel, onSubmit, dispatchedSeedScope, anchor, isMultiScope, entries } = props;

  // const buttonRef = useFocusButtonRef();
  const [loading, setLoading] = useState(false);
  const [mutationPending, setMutationPending] = useState(false);

  const [selectedPlanId, setPlanId] = useSelectedPlanId(commands);
  const currentCommands = useCurrentCommands(commands, selectedPlanId);
  const [selectedTime, setSelectedTime] = useSelectedTime(currentCommands);
  const handleChangePlanId = useSetPlanId(commands, setPlanId);
  const versionOptions = useCommandToDropdown(commands, selectedPlanId, selectedTime, entries);
  const timeOptions = useCommandToTimeDropdown(currentCommands, entries);
  const [selectedCommand, setSelectedCommand] = useSelectedCommand(currentCommands);
  const handleChangeCommand = useHandleChangeCommand(setSelectedCommand);
  const handleChangeTime = useHandleChangeTime(selectedTime, setSelectedTime);

  useEffect(() => {
    if (isEmpty(commands) && isUndefined(anchor)) {
      setLoading(false); // no data, doesn't need to remain loading
    } else if (isEmpty(commands)) {
      setLoading(true);
    }
  }, [commands, anchor]);

  const handleSubmit = useCallback(async () => {
    if (currentCommands && selectedCommand) {
      const maybeFoundCommand = keyToCommand(
        selectedCommand,
        // this won't work with import commands that aren't actuals that match the time dropdown options
        currentCommands.commands.filter((c) => c.displayTime === selectedTime),
      );
      if (!maybeFoundCommand) {
        return;
      }
      setMutationPending(true);
      const maybeSeed = await dispatchedSeedScope(maybeFoundCommand.command);

      if (maybeSeed.type === seedScope.rejected.type) {
        // failure, toast and don't go forward
        toast.error('An error occured seeding your scope');
      } else {
        if (!isMultiScope) {
          onSubmit();
        }
      }
      setMutationPending(false);
    }
  }, [currentCommands, selectedCommand, selectedTime, dispatchedSeedScope, isMultiScope, onSubmit]);

  const handleEnterPress = useHandleKeyPress(handleSubmit);

  return (
    <>
      <div className="initialize-plan">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box p={2}>
              <Typography variant="h5" className="initialize-plan-header" gutterBottom>
                Overwrite your WP with the selected version
              </Typography>
              <Typography variant="subtitle1" paragraph>
                This will erase your current plan values (though you are able to use "Undo" to revert).
              </Typography>

              {commands && dimensionLabel && anchor ? (
                <AnchorRadioSelect
                  labelDimenion={dimensionLabel}
                  anchor={anchor}
                  onUpdateAnchorSelections={handleChangePlanId}
                />
              ) : null}

              <Box mt={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Select the Seed basis Plan Period
                </Typography>
                <Autocomplete
                  fullWidth
                  options={timeOptions}
                  className="no-border-style-input"
                  popupIcon={<KeyboardArrowDown className="teal-arrow" />}
                  getOptionLabel={(option) => option.text}
                  loading={loading}
                  disabled={!selectedTime}
                  value={timeOptions.find((o) => o.value === selectedTime) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleChangeTime(event, newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" data-qa="reseed-period-dropdown" />
                  )}
                />
              </Box>

              <Box mt={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Select the Seed Basis Plan Version
                </Typography>
                <Autocomplete
                  fullWidth
                  options={versionOptions}
                  className="no-border-style-input"
                  popupIcon={<KeyboardArrowDown className="teal-arrow" />}
                  getOptionLabel={(option) => option.text}
                  loading={loading}
                  disabled={!selectedCommand}
                  value={versionOptions.find((o) => o.value === selectedCommand) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleChangeCommand(event, newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" data-qa="reseed-version-dropdown" />
                  )}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </div>

      <CardActions>
        <SquareActionButton
          className="button"
          type="secondary"
          onClick={onCancel}
          style={{ marginRight: '0.5rem' }}
          text="Close"
        />

        <SquareActionButton
          type="primary"
          className="import-version-modal-button button"
          dataQa="reseed-reseed-btn-reseed"
          onClick={handleSubmit}
          disabled={mutationPending || loading}
          loading={mutationPending || loading}
          onKeyPress={handleEnterPress}
          text="Submit"
        />
      </CardActions>
    </>
  );
}

// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(ReseedPlanModal);
